import { useCallback, useEffect, useState } from "react"
import { useSearchParams } from "react-router"
import { CalendarDaysIcon, CheckCircleIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline"
import EnkPageContainer from "../components/page-container"
import EnkLoader from "../components/common/loader"
import { useMsgDialog } from "../dialogs/use-msgdialog"
import { EnkApiError } from "../api/do-request"
import { classNames } from "../utils/styling-utils"
import DagCard, { formatDate } from "../components/planner/dag-card"
import {
  EnkIWPServerPlanner,
  TEnkIWPAvailabilityDay,
  TEnkIWPAvailabilityResult,
  TEnkIWPPlanBonInfo,
  TEnkIWPPlanOverview,
  TEnkIWPTimeSlot,
} from "../api/api-iwp-server"

/** Stap in het planproces */
type PlanStep = "overview" | "selectSlot" | "done"

export default function EnkPlanner() {
  const { messageDialog } = useMsgDialog()
  const [searchParams, setSearchParams] = useSearchParams()

  const [step, setStep] = useState<PlanStep>("overview")
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Overzicht
  const [overview, setOverview] = useState<TEnkIWPPlanOverview | null>(null)
  const [selectedBon, setSelectedBon] = useState<TEnkIWPPlanBonInfo | null>(null)

  // Beschikbaarheid
  const [availability, setAvailability] = useState<TEnkIWPAvailabilityResult | null>(null)
  const [selectedSlot, setSelectedSlot] = useState<{ dag: TEnkIWPAvailabilityDay; slot: TEnkIWPTimeSlot } | null>(null)

  // Resultaat
  const [resultMessage, setResultMessage] = useState("")
  const [bevestigingVereist, setBevestigingVereist] = useState(false)

  // Laad overzicht van alle bonnen
  const loadOverview = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const result = await EnkIWPServerPlanner.getOverview()
      setOverview(result)
      setStep("overview")
    } catch (err) {
      console.error("Failed to load overview:", err)
      const msg = err instanceof EnkApiError ? err.description : "Fout bij het laden van het overzicht."
      setError(msg)
    }
    setLoading(false)
  }, [])

  useEffect(() => {
    void loadOverview()
  }, [loadOverview])

  // Haal beschikbaarheid op voor een bon
  const fetchAvailability = useCallback(async (bon: TEnkIWPPlanBonInfo) => {

    setSelectedBon(bon)
    setLoading(true)
    setError(null)
    setAvailability(null)
    setSelectedSlot(null)

    try {
      const result = await EnkIWPServerPlanner.getAvailability({ opdrachtbonnr: bon.opdrachtbonnr })
      setAvailability(result)
      setStep("selectSlot")
    } catch (err) {
      if (err instanceof EnkApiError && err.httpStatusCode === 409) {
        setError(err.description || "Deze opdrachtbon is al ingepland.")
      } else {
        console.error("Failed to fetch availability:", err)
        setError("Fout bij het ophalen van beschikbaarheid.")
      }
    }
    setLoading(false)
  }, [])

  // Auto-selecteer bon op basis van ?bon=<bonnr> query parameter
  // (gebruikt door 'Openen in IWP' knop in ENK desktop)
  useEffect(() => {
    if (step !== "overview" || !overview) return
    const bonnr = searchParams.get("bon")
    if (!bonnr) return

    // Eenmalig consumeren: param uit URL halen zodat refresh niet weer triggert
    setSearchParams({}, { replace: true })

    const bon = overview.bonnen.find((b) => b.opdrachtbonnr === bonnr)
    if (!bon) {
      setError(`Opdrachtbon ${bonnr} niet gevonden in het overzicht.`)
      return
    }
    if (bon.status !== "pbsOpenstaand") {
      const statusLabel = statusConfig[bon.status]?.label ?? bon.status
      setError(`Opdrachtbon ${bonnr} is al ingepland (${statusLabel}).`)
      return
    }
    void fetchAvailability(bon)
  }, [overview, step, searchParams, setSearchParams, fetchAvailability])

  // Plan de afspraak in
  const handleSchedule = useCallback(async () => {
    if (!selectedSlot || !availability || !selectedBon) return

    messageDialog(
      "confirmation",
      `Wilt u een afspraak inplannen op ${formatDate(selectedSlot.dag.datum)} van ${selectedSlot.slot.beginTijd} tot ${selectedSlot.slot.eindTijd}?`,
      ["Inplannen", "Annuleren"],
      async (button) => {
        if (button !== "Inplannen") return

        setLoading(true)
        setError(null)

        try {
          const result = await EnkIWPServerPlanner.schedule({
            opdrachtbonnr: selectedBon.opdrachtbonnr,
            datum: selectedSlot.dag.datum,
            tijdblokId: selectedSlot.slot.tijdblokId,
          })

          if (result.success) {
            setResultMessage(result.melding ?? "Afspraak succesvol ingepland.")
            setBevestigingVereist(result.bevestigingVereist)
            setStep("done")
          } else {
            setError(result.melding ?? "Er is een fout opgetreden bij het inplannen.")
          }
        } catch (err) {
          console.error("Failed to schedule:", err)
          setError("Fout bij het inplannen van de afspraak.")
        }
        setLoading(false)
      }
    )
  }, [selectedSlot, availability, selectedBon, messageDialog])

  // Terug naar overzicht
  const handleBackToOverview = () => {
    setSelectedBon(null)
    setAvailability(null)
    setSelectedSlot(null)
    setError(null)
    setStep("overview")
  }

  // Na inplannen: herlaad overzicht
  const handleBackAfterDone = useCallback(async () => {
    setSelectedBon(null)
    setAvailability(null)
    setSelectedSlot(null)
    setError(null)
    setResultMessage("")
    await loadOverview()
  }, [loadOverview])

  return (
    <EnkPageContainer>
      <div className="max-w-4xl mx-auto">
        {loading && <EnkLoader />}

        {/* Error melding */}
        {error && (
          <div className="text-red-500 text-sm font-semibold py-2 px-4 bg-red-50 rounded-md mb-4 flex items-start gap-2">
            <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {/* Overzicht: alle bonnen van de relatie */}
        {step === "overview" && !loading && overview && (
          <div className="mt-4">
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-1">
                <CalendarDaysIcon className="h-6 w-6 text-primary" />
                <h1 className="text-xl font-semibold text-gray-900">Afspraken plannen</h1>
              </div>
              {overview.relatieNaam && (
                <p className="text-sm text-gray-500 ml-9">{overview.relatieNaam}</p>
              )}
            </div>

            {overview.bonnen.length === 0 ? (
              <div className="rounded-lg border border-gray-200 bg-white p-8 shadow-sm text-center">
                <CalendarDaysIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-sm text-gray-500">Er zijn geen opdrachten om in te plannen.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {overview.bonnen.map((bon) => (
                  <BonCard
                    key={bon.opdrachtbonnr}
                    bon={bon}
                    onPlannen={() => void fetchAvailability(bon)}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Tijdblok selecteren */}
        {step === "selectSlot" && availability && selectedBon && (
          <div className="mt-4">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {availability.omschrijving ?? `Opdrachtbon ${availability.opdrachtbonnr}`}
                </h2>
                <p className="text-sm text-gray-500">Kies een beschikbaar tijdblok</p>
              </div>
              <button
                onClick={handleBackToOverview}
                className="rounded-md bg-white px-3 py-1.5 text-sm font-semibold text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
              >
                Terug
              </button>
            </div>

            {availability.dagen.length === 0 && (
              <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                <CalendarDaysIcon className="h-12 w-12 mb-3" />
                <p className="text-sm font-medium text-gray-500">Geen beschikbare dagen gevonden</p>
                <p className="text-xs text-gray-400 mt-1">Er zijn momenteel geen tijdblokken beschikbaar voor deze opdrachtbon.</p>
              </div>
            )}

            <div className="space-y-4">
              {availability.dagen.map((dag) => (
                <DagCard
                  key={dag.datum}
                  dag={dag}
                  selectedSlot={selectedSlot}
                  onSelectSlot={(slot) => setSelectedSlot({ dag, slot })}
                />
              ))}
            </div>

            {/* Inplannen knop */}
            {selectedSlot && (
              <div className="sticky bottom-0 bg-white border-t border-gray-200 py-4 mt-6 -mx-4 px-4 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                <div className="flex items-center justify-between max-w-4xl mx-auto">
                  <div className="text-sm text-gray-700">
                    <span className="font-medium">{selectedSlot.dag.dagNaam}</span>{" "}
                    {formatDate(selectedSlot.dag.datum)} &mdash;{" "}
                    <span className="font-medium">{selectedSlot.slot.omschrijving ?? `${selectedSlot.slot.beginTijd} - ${selectedSlot.slot.eindTijd}`}</span>
                  </div>
                  <button
                    onClick={() => void handleSchedule()}
                    disabled={loading}
                    className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Inplannen
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Bevestiging */}
        {step === "done" && (
          <div className="mt-4">
            <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm text-center">
              <CheckCircleIcon className="h-12 w-12 text-green-500 mx-auto mb-4" />
              <h2 className="text-lg font-semibold text-gray-900 mb-2">
                {bevestigingVereist ? "Aanvraag ingediend" : "Afspraak bevestigd"}
              </h2>
              <p className="text-sm text-gray-600 mb-6">{resultMessage}</p>
              {bevestigingVereist && (
                <p className="text-xs text-gray-400 mb-6">
                  Uw afspraak moet nog bevestigd worden door de installateur. U ontvangt een e-mail zodra dit is gebeurd.
                </p>
              )}
              <button
                onClick={() => void handleBackAfterDone()}
                className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/90"
              >
                Terug naar overzicht
              </button>
            </div>
          </div>
        )}
      </div>
    </EnkPageContainer>
  )
}

/** Status badge kleuren */
const statusConfig: Record<string, { label: string; bg: string; text: string }> = {
  pbsOpenstaand: { label: "Nog niet ingepland", bg: "bg-gray-100", text: "text-gray-600" },
  pbsAangevraagd: { label: "Wacht op bevestiging", bg: "bg-yellow-100", text: "text-yellow-700" },
  pbsBevestigd: { label: "Bevestigd", bg: "bg-green-100", text: "text-green-700" },
  pbsGeweigerd: { label: "Geweigerd", bg: "bg-red-100", text: "text-red-700" },
}

/** Kaart voor een bon in het overzicht */
function BonCard({ bon, onPlannen }: { bon: TEnkIWPPlanBonInfo; onPlannen: () => void }) {
  const config = statusConfig[bon.status] ?? statusConfig.pbsOpenstaand
  const kanInplannen = bon.status === "pbsOpenstaand"

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-sm font-semibold text-gray-900">{bon.opdrachtbonnr}</span>
            <span className={classNames("inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium", config.bg, config.text)}>
              {bon.statusTekst || config.label}
            </span>
          </div>
          <p className="text-sm text-gray-600 truncate">{bon.omschrijving}</p>
          {bon.datum && bon.tijdblok && (
            <p className="text-xs text-gray-400 mt-1">
              {formatDate(bon.datum)} &mdash; {bon.tijdblok}
            </p>
          )}
        </div>
        {kanInplannen && (
          <button
            onClick={onPlannen}
            className="flex-shrink-0 rounded-md bg-primary px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-primary/90"
          >
            Plan in
          </button>
        )}
      </div>
    </div>
  )
}
